import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Building2, Package, ShoppingCart, DollarSign, Truck,
  Upload, FileSpreadsheet, Bot, Sparkles, CheckCircle,
  AlertTriangle, Clock, Eye, Edit, Trash2, Plus, X,
  ArrowRight, BarChart3, FileText, Image, Tag, Hash,
  Barcode, FolderOpen, Zap, Play, Check, Search,
  Wallet, Banknote, ArrowUpRight, ArrowDownLeft, History,
  QrCode, User, Phone, Mail, MapPin, ScanLine, Loader2
} from "lucide-react";
import WMSPanel from "./WMSPanel";
import { carteiraService } from "../../services/api";
import { NotificationToggle, NotificationPrompt } from "./NotificationToggle";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Fornecedor = () => {
  const [activeTab, setActiveTab] = useState('pedidos'); // pedidos, produtos, importar, financeiro
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showCarteiraModal, setShowCarteiraModal] = useState(false);
  const [showPerfilModal, setShowPerfilModal] = useState(false);
  const [carteiraTab, setCarteiraTab] = useState('resumo');
  const [importStep, setImportStep] = useState(1);
  const [importProgress, setImportProgress] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [useAI, setUseAI] = useState(false);
  const [valorSaque, setValorSaque] = useState('');
  const [carteiraLoading, setCarteiraLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [pedidosReais, setPedidosReais] = useState([]);
  const [registrandoEnvio, setRegistrandoEnvio] = useState(null);
  
  // ACHADO DA REVISÃO: era um ID fixo ("fornecedor-001"), nunca o do
  // usuário realmente logado - com o backend exigindo que o dono do
  // recurso bata com o token, isso quebrava toda chamada de
  // carteira/perfil com 403. Agora usa o ID real de quem está logado.
  const FORNECEDOR_ID = authService.getUser()?.id || "";
  
  // Dados Bancários
  const [dadosBancarios, setDadosBancarios] = useState({
    banco: '',
    agencia: '',
    conta: '',
    tipoConta: 'corrente',
    chavePix: '',
    tipoChavePix: 'cnpj'
  });

  // Dados do Fornecedor (editáveis)
  const [fornecedorData, setFornecedorData] = useState({
    nome: "",
    cnpj: "",
    razaoSocial: "",
    email: "",
    telefone: "",
    foto: null,
    cidade: "",
    estado: "",
    endereco: "",
    cep: "",
    pedidosHoje: 0,
    pedidosPendentes: 0,
    receitaGarantida: 0,
    pedidosAtrasados: 0,
    produtoTop: "",
    faturamentoMes: 0,
    comissoesPagas: 0,
    aReceber: 0,
    // Carteira - será carregado da API
    saldoDisponivel: 0,
    lucroDisponivel: 0,
    saldoBloqueado: 0
  });

  // Histórico da Carteira - será carregado da API
  const [historicoCarteira, setHistoricoCarteira] = useState([]);

  // Carregar carteira do fornecedor ao abrir modal
  useEffect(() => {
    if (showCarteiraModal) {
      carregarCarteira();
    }
  }, [showCarteiraModal]);

  // Carregar dashboard real do MongoDB ao montar
  useEffect(() => {
    carregarDashboardReal();
  }, []);

  // ACHADO DA AUDITORIA ENTERPRISE: os botoes Etiqueta/Enviar checavam um
  // status ("pago") que nao existe no fluxo real - nunca apareciam.
  // Corrigido para checar o status real (pendente) e para o botao Enviar
  // registrar de verdade no backend, sem mexer no status oficial que so
  // o admin controla.
  // Upload de foto de perfil - mesmo endpoint e padrao usado no afiliado
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const inputFotoRef = React.useRef(null);

  const selecionarFoto = () => {
    inputFotoRef.current?.click();
  };

  const enviarFoto = async (evento) => {
    const arquivo = evento.target.files?.[0];
    if (!arquivo) return;

    setEnviandoFoto(true);
    try {
      const formData = new FormData();
      formData.append("file", arquivo);
      const resp = await authService.fetchAuth(
        `${BACKEND_URL}/api/perfil/foto/${FORNECEDOR_ID}`,
        { method: "POST", body: formData }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao enviar foto");

      setFornecedorData((prev) => ({ ...prev, foto: `${BACKEND_URL}${data.avatar_url}` }));
      toast.success("Foto de perfil atualizada!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEnviandoFoto(false);
    }
  };

  const registrarEnvio = async (pedido) => {
    setRegistrandoEnvio(pedido.id);
    try {
      const codigoRastreio = window.prompt("Código de rastreio (opcional, pode deixar em branco):", "");
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/business/pedido/${pedido.id}/registrar-envio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigo_rastreio: codigoRastreio || null }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || "Erro ao registrar envio");
      }
      toast.success("Envio registrado! O admin será avisado para confirmar a entrega.");
      await carregarDashboardReal();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRegistrandoEnvio(null);
    }
  };

  const carregarDashboardReal = async () => {
    try {
      setDashboardLoading(true);
      // CORRIGIDO (achado da sessão de deploy): antes chamava
      // /api/fornecedor/dashboard/{id} - um endpoint antigo, SEM
      // autenticação e com um bug real: se o fornecedor não tinha nenhum
      // pedido ainda, ele mostrava os dados agregados de TODOS os
      // fornecedores como se fossem dele (vazamento de dado de negócio
      // entre fornecedores). Agora usa o endpoint correto e seguro
      // (exige login, confere que é o dono, nunca mistura dado de outro
      // fornecedor).
      const response = await fetch(`${BACKEND_URL}/api/business/fornecedor/dashboard/${FORNECEDOR_ID}`);
      if (response.ok) {
        const data = await response.json();
        const kpis = data.kpis;
        const forn = data.fornecedor || {};
        setFornecedorData(prev => ({
          ...prev,
          // Perfil (também eram valores fixos de exemplo antes)
          nome: forn.nome_fantasia || prev.nome,
          razaoSocial: forn.razao_social || prev.razaoSocial,
          cnpj: forn.cnpj || prev.cnpj,
          email: forn.email || prev.email,
          telefone: forn.telefone || prev.telefone,
          // KPIs reais
          pedidosHoje: kpis.pedidos_pendentes,
          pedidosPendentes: kpis.pedidos_pendentes,
          receitaGarantida: kpis.receitas_pendentes,
          pedidosAtrasados: kpis.pedidos_atrasados,
          produtoTop: kpis.top_produto?.produto_nome || "N/A",
          faturamentoMes: kpis.faturamento_mes,
          comissoesPagas: 0,
          aReceber: kpis.receitas_pendentes
        }));
        if (data.pedidos_recentes && data.pedidos_recentes.length > 0) {
          setPedidosReais(data.pedidos_recentes);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard fornecedor:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  const carregarCarteira = async () => {
    try {
      setCarteiraLoading(true);
      const carteira = await carteiraService.getCarteira(FORNECEDOR_ID, 'fornecedor');
      
      setFornecedorData(prev => ({
        ...prev,
        saldoDisponivel: carteira.saldo_disponivel || 0,
        lucroDisponivel: carteira.lucro_disponivel || 0,
        saldoBloqueado: carteira.saldo_bloqueado || 0
      }));

      if (carteira.dados_bancarios) {
        setDadosBancarios({
          banco: carteira.dados_bancarios.banco || '',
          agencia: carteira.dados_bancarios.agencia || '',
          conta: carteira.dados_bancarios.conta || '',
          tipoConta: carteira.dados_bancarios.tipo_conta || 'corrente',
          chavePix: carteira.dados_bancarios.chave_pix || '',
          tipoChavePix: carteira.dados_bancarios.tipo_chave_pix || 'cnpj'
        });
      }

      // Carregar histórico
      const historico = await carteiraService.getHistorico(FORNECEDOR_ID, 'fornecedor');
      setHistoricoCarteira(historico.map(item => ({
        id: item.id,
        tipo: item.tipo,
        valor: item.valor,
        descricao: item.descricao,
        data: new Date(item.created_at).toLocaleString('pt-BR')
      })));
    } catch (error) {
      console.error('Erro ao carregar carteira:', error);
      toast.error('Erro ao carregar dados da carteira');
    } finally {
      setCarteiraLoading(false);
    }
  };

  // Categorias disponíveis
  const categorias = [
    "Eletrônicos", "Smartphones", "Notebooks", "TVs", "Áudio",
    "Moda Masculina", "Moda Feminina", "Calçados", "Acessórios",
    "Casa e Decoração", "Móveis", "Eletrodomésticos",
    "Esportes", "Beleza", "Brinquedos", "Automotivo"
  ];

  // NOTA: lista de pedidos de exemplo removida (achado da sessão de
  // deploy) - a tela agora usa "pedidosReais", carregado de verdade da
  // API em carregarDashboardReal.

  // Produtos do Fornecedor
  const [produtos, setProdutos] = useState([
    {
      id: 1,
      nome: "Notebook Gamer RTX 4060",
      sku: "NB-RTX4060-001",
      skuPai: "NB-GAMER",
      ean: "7891234567890",
      eanPai: "789123456",
      categoria: "Notebooks",
      preco: 4599.90,
      estoque: 45,
      status: "ativo",
      imagem: "https://images.unsplash.com/photo-1603302576837-37561b2e2302?w=200",
      descricao: "Notebook Gamer com RTX 4060, 16GB RAM, SSD 512GB, Tela 15.6\" Full HD 144Hz",
      temDescricao: true,
      temImagem: true
    },
    {
      id: 2,
      nome: "Smart TV 55\" QLED 4K",
      sku: "TV-QLED55-002",
      skuPai: "TV-QLED",
      ean: "7891234567891",
      eanPai: "789123457",
      categoria: "TVs",
      preco: 2899.90,
      estoque: 23,
      status: "ativo",
      imagem: "https://images.unsplash.com/photo-1593359677879-a4bb92f829d1?w=200",
      descricao: "Smart TV QLED 55 polegadas, 4K UHD, HDR10+, Smart Hub, Gaming Mode",
      temDescricao: true,
      temImagem: true
    },
    {
      id: 3,
      nome: "Smartphone 5G 256GB",
      sku: "SP-5G256-003",
      skuPai: "SP-5G",
      ean: "7891234567892",
      eanPai: "789123458",
      categoria: "Smartphones",
      preco: 2399.90,
      estoque: 67,
      status: "ativo",
      imagem: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=200",
      descricao: "Smartphone 5G, 256GB armazenamento, Câmera 108MP, Tela AMOLED 6.7\"",
      temDescricao: true,
      temImagem: true
    },
    {
      id: 4,
      nome: "Fone Bluetooth",
      sku: "",
      skuPai: "FN-BT",
      ean: "",
      eanPai: "789123459",
      categoria: "Áudio",
      preco: 599.90,
      estoque: 120,
      status: "incompleto",
      imagem: "",
      descricao: "",
      temDescricao: false,
      temImagem: false
    }
  ]);

  // Novo produto
  const [newProduct, setNewProduct] = useState({
    nome: "", categoria: "", skuPai: "", eanPai: "", preco: "", estoque: "",
    descricao: "", usarIA: false, marca: "", modelo: "", condicao: "new",
    peso: "", altura: "", largura: "", comprimento: "",
    fichaTecnica: "", imagens: [], marketplaces: [],
    semGtin: false
  });
  const [savingProduct, setSavingProduct] = useState(false);

  // Marketplaces disponíveis para publicação
  const marketplacesDisponiveis = [
    { id: 'mercado_livre', name: 'Mercado Livre' },
    { id: 'shopee', name: 'Shopee' },
    { id: 'amazon', name: 'Amazon' },
    { id: 'magalu', name: 'Magazine Luiza' },
    { id: 'americanas', name: 'Americanas' },
    { id: 'casas_bahia', name: 'Casas Bahia' },
    { id: 'shein', name: 'Shein' },
    { id: 'aliexpress', name: 'AliExpress' },
    { id: 'havan', name: 'Havan' },
    { id: 'submarino', name: 'Submarino' },
  ];

  const handleSaveProduct = async () => {
    if (!newProduct.nome || !newProduct.categoria || !newProduct.preco || !newProduct.estoque) {
      toast.error('Preencha os campos obrigatorios: Nome, Categoria, Preco e Estoque');
      return;
    }
    if (!newProduct.eanPai && !newProduct.semGtin) {
      toast.error('Informe o EAN/GTIN ou marque "SEM GTIN"');
      return;
    }
    try {
      setSavingProduct(true);
      const produtoData = {
        nome: newProduct.nome,
        title: `${newProduct.nome} ${newProduct.marca} ${newProduct.modelo}`.trim(),
        categoria: newProduct.categoria,
        sku: newProduct.skuPai || `SKU-${Date.now()}`,
        ean: newProduct.semGtin ? 'SEM GTIN' : newProduct.eanPai,
        preco: parseFloat(newProduct.preco),
        preco_venda: parseFloat(newProduct.preco),
        price: parseFloat(newProduct.preco),
        estoque: parseInt(newProduct.estoque),
        stock: parseInt(newProduct.estoque),
        descricao: newProduct.descricao,
        description: newProduct.descricao,
        marca: newProduct.marca,
        modelo: newProduct.modelo,
        condicao: newProduct.condicao,
        peso: newProduct.peso,
        dimensoes: { altura: newProduct.altura, largura: newProduct.largura, comprimento: newProduct.comprimento },
        ficha_tecnica: newProduct.fichaTecnica,
        marketplaces: newProduct.marketplaces,
        fornecedor_id: FORNECEDOR_ID,
        fornecedor_nome: fornecedorData.nome || 'TechStore',
        usar_ia: newProduct.usarIA,
        ativo: true,
        autorizado: true,
        vendas: 0,
        status: 'ativo'
      };

      const response = await fetch(`${BACKEND_URL}/api/fornecedor/${FORNECEDOR_ID}/produto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(produtoData)
      });

      if (response.ok) {
        toast.success('Produto cadastrado com sucesso! Disponivel em Meus Produtos.');
        setShowAddProductModal(false);
        setNewProduct({
          nome: "", categoria: "", skuPai: "", eanPai: "", preco: "", estoque: "",
          descricao: "", usarIA: false, marca: "", modelo: "", condicao: "new",
          peso: "", altura: "", largura: "", comprimento: "",
          fichaTecnica: "", imagens: [], marketplaces: [], semGtin: false
        });
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao cadastrar produto');
      }
    } catch (error) {
      toast.error('Erro ao cadastrar produto');
    } finally {
      setSavingProduct(false);
    }
  };

  // Status badges
  const getStatusBadge = (status) => {
    const configs = {
      pendente: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "Pendente" },
      pago: { bg: "bg-blue-500/20", text: "text-blue-400", label: "Pago" },
      enviado: { bg: "bg-purple-500/20", text: "text-purple-400", label: "Enviado" },
      entregue: { bg: "bg-green-500/20", text: "text-green-400", label: "Entregue" },
      ativo: { bg: "bg-green-500/20", text: "text-green-400", label: "Ativo" },
      incompleto: { bg: "bg-orange-500/20", text: "text-orange-400", label: "Incompleto" },
      inativo: { bg: "bg-gray-500/20", text: "text-gray-400", label: "Inativo" }
    };
    const config = configs[status] || configs.pendente;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Simular importação com IA
  const startImport = async () => {
    setImportProgress({ step: 0, message: "Iniciando importação..." });
    
    const steps = useAI ? [
      "📂 Analisando arquivo de catálogo...",
      "🔍 Identificando produtos e categorias...",
      "🤖 IA gerando títulos otimizados...",
      "📝 IA criando descrições persuasivas...",
      "🖼️ IA processando e otimizando imagens...",
      "🏷️ Gerando SKUs e códigos EAN...",
      "✅ Finalizando importação..."
    ] : [
      "📂 Analisando arquivo de catálogo...",
      "🔍 Validando dados do arquivo...",
      "🏷️ Verificando SKUs e EANs...",
      "✅ Finalizando importação..."
    ];

    for (let i = 0; i < steps.length; i++) {
      await new Promise(res => setTimeout(res, 1500));
      setImportProgress({ step: i + 1, total: steps.length, message: steps[i] });
    }

    await new Promise(res => setTimeout(res, 1000));
    setImportProgress(null);
    setShowImportModal(false);
    setImportStep(1);
    toast.success("🎉 Catálogo importado com sucesso! " + (useAI ? "A IA gerou todas as descrições e títulos automaticamente." : ""));
  };

  // Funções da Carteira Digital
  const handleSaque = async () => {
    const valor = parseFloat(valorSaque);
    if (isNaN(valor) || valor <= 0) {
      toast.error("Digite um valor válido para saque");
      return;
    }
    
    if (valor > fornecedorData.lucroDisponivel) {
      toast.error("Valor de saque maior que o lucro disponível");
      return;
    }
    
    if (!dadosBancarios.chavePix) {
      toast.error("Configure seus dados bancários antes de sacar");
      setCarteiraTab('dados');
      return;
    }
    
    try {
      setCarteiraLoading(true);
      await carteiraService.sacar(FORNECEDOR_ID, 'fornecedor', valor);
      
      // Recarregar carteira
      await carregarCarteira();
      setValorSaque('');
      toast.success(`✅ Saque de R$ ${valor.toFixed(2)} solicitado! Será processado em até 24h.`);
    } catch (error) {
      toast.error(error.message || 'Erro ao solicitar saque');
    } finally {
      setCarteiraLoading(false);
    }
  };

  const salvarDadosBancarios = async () => {
    if (!dadosBancarios.chavePix) {
      toast.error("Informe pelo menos uma chave PIX");
      return;
    }
    
    try {
      setCarteiraLoading(true);
      await carteiraService.atualizarDadosBancarios(FORNECEDOR_ID, 'fornecedor', dadosBancarios);
      toast.success("✅ Dados bancários salvos com sucesso!");
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar dados bancários');
    } finally {
      setCarteiraLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-950">
      <div className="flex-1">
        <div className="p-4 lg:p-6 max-w-7xl mx-auto">
          {/* Header com Avatar e Carteira */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setShowPerfilModal(true)}
              >
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded">FORNECEDOR</span>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">{fornecedorData.nome}</h1>
                </div>
                <p className="text-gray-500 text-sm">Clique no ícone para editar dados da empresa</p>
              </div>
            </div>

            {/* Carteira Digital Fornecedor */}
            <div className="flex items-center gap-2">
              <NotificationToggle usuarioId={FORNECEDOR_ID} tipoUsuario="fornecedor" variant="icon" />
              <Card 
                className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30 p-3 cursor-pointer hover:border-green-400 transition-all"
                onClick={() => setShowCarteiraModal(true)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Saldo para Sacar</p>
                    <p className="text-green-400 text-lg font-bold">R$ {fornecedorData.lucroDisponivel.toFixed(2)}</p>
                  </div>
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-8">
                    <Banknote className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </div>
          </div>

          {/* Prompt de Notificações */}
          <NotificationPrompt usuarioId={FORNECEDOR_ID} tipoUsuario="fornecedor" />

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 border-none">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-xs">Pedidos Hoje</p>
                  <p className="text-white text-2xl font-bold">{fornecedorData.pedidosHoje}</p>
                  <p className="text-blue-200 text-xs">{fornecedorData.pedidosPendentes} pendentes</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-white/50" />
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 border-none">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-xs">Receitas Garantidas</p>
                  <p className="text-white text-xl font-bold">R$ {fornecedorData.receitaGarantida.toLocaleString()}</p>
                  <p className="text-green-200 text-xs">Repasse pendente</p>
                </div>
                <DollarSign className="w-8 h-8 text-white/50" />
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-orange-500 to-red-600 p-3 border-none">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-xs">Pedidos Atrasados</p>
                  <p className="text-white text-2xl font-bold">{fornecedorData.pedidosAtrasados}</p>
                  <p className="text-orange-200 text-xs">Enviar urgente!</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-white/50" />
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-violet-600 p-3 border-none">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-xs">Produto Top</p>
                  <p className="text-white text-lg font-bold">{fornecedorData.produtoTop}</p>
                  <p className="text-purple-200 text-xs">45 vendas este mês</p>
                </div>
                <Package className="w-8 h-8 text-white/50" />
              </div>
            </Card>
          </div>

          {/* Aviso de Importação */}
          <Card className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 border-orange-500/30 p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-gray-900 dark:text-white font-bold mb-1">🤖 Importe seu Catálogo - O Robô Faz o Resto!</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                  Envie seu catálogo por categoria. Se não tiver descrições, títulos ou imagens, 
                  <strong className="text-orange-500"> a IA cria tudo automaticamente</strong> para você!
                </p>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setShowImportModal(true)}
                    className="bg-orange-500 hover:bg-orange-600 h-9"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Importar Catálogo
                  </Button>
                  <Button 
                    onClick={() => setShowAddProductModal(true)}
                    variant="outline"
                    className="border-orange-500 text-orange-500 hover:bg-orange-500/10 h-9"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Produto Manual
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            <Button
              onClick={() => setActiveTab('pedidos')}
              className={`h-9 ${activeTab === 'pedidos' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Pedidos
            </Button>
            <Button
              onClick={() => setActiveTab('wms')}
              className={`h-9 ${activeTab === 'wms' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              <ScanLine className="w-4 h-4 mr-2" />
              WMS / Separação
            </Button>
            <Button
              onClick={() => setActiveTab('produtos')}
              className={`h-9 ${activeTab === 'produtos' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              <Package className="w-4 h-4 mr-2" />
              Meus Produtos
            </Button>
            <Button
              onClick={() => setActiveTab('financeiro')}
              className={`h-9 ${activeTab === 'financeiro' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Financeiro
            </Button>
          </div>

          {/* TAB: Pedidos */}
          {activeTab === 'pedidos' && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-orange-500" />
                Pedidos Recebidos
              </h2>

              {/* ACHADO DA SESSÃO DE DEPLOY: esta tela sempre mostrava uma
                  lista de 3 pedidos fixos de exemplo (PED-A1B2C3, Carlos
                  M. etc.) - mesmo depois de "pedidosReais" já estar sendo
                  buscado corretamente da API real, ele nunca era usado
                  aqui na tela. Agora usa os dados reais. */}
              {pedidosReais.length === 0 && (
                <p className="text-gray-400 text-sm">Nenhum pedido recebido ainda.</p>
              )}
              <div className="space-y-3">
                {pedidosReais.map((pedido) => (
                  <Card key={pedido.id} className="bg-gray-800/50 border-gray-700 p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-bold">#{pedido.codigo || pedido.id}</span>
                          {getStatusBadge(pedido.status)}
                        </div>
                        <p className="text-white font-medium">{pedido.produto_nome}</p>
                        <p className="text-gray-400 text-sm">
                          {pedido.created_at ? new Date(pedido.created_at).toLocaleString('pt-BR') : ''}
                        </p>
                      </div>
                      
                      <div className="lg:w-48">
                        <p className="text-xs text-gray-500">Cliente</p>
                        <p className="text-white text-sm">{pedido.cliente_nome}</p>
                        <p className="text-gray-400 text-xs truncate">{pedido.endereco_entrega || ''}</p>
                      </div>

                      <div className="lg:w-32">
                        <p className="text-xs text-gray-500">Afiliado</p>
                        <p className="text-purple-400 text-sm">{pedido.afiliado_id}</p>
                      </div>

                      <div className="lg:w-28 lg:text-right">
                        <p className="text-xs text-gray-500">Valor</p>
                        <p className="text-green-400 text-lg font-bold">R$ {(pedido.valor_fornecedor || 0).toFixed(2)}</p>
                      </div>

                      <div className="flex gap-2">
                        {pedido.status === 'pendente' && !pedido.enviado_pelo_fornecedor && (
                          <>
                            <Button
                              size="sm" variant="outline" className="border-gray-700 h-8"
                              onClick={() => window.print()}
                              title="Imprime a página atual - etiqueta simples baseada nos dados do pedido"
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              Etiqueta
                            </Button>
                            <Button
                              size="sm" className="bg-green-500 hover:bg-green-600 h-8"
                              onClick={() => registrarEnvio(pedido)}
                              disabled={registrandoEnvio === pedido.id}
                            >
                              <Truck className="w-3 h-3 mr-1" />
                              {registrandoEnvio === pedido.id ? "..." : "Enviar"}
                            </Button>
                          </>
                        )}
                        {pedido.status === 'pendente' && pedido.enviado_pelo_fornecedor && (
                          <span className="text-purple-400 text-xs">Você já despachou - aguardando confirmação de entrega pelo admin</span>
                        )}
                        {pedido.status === 'entregue' && (
                          <span className="text-green-400 text-xs">Entregue</span>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* TAB: WMS / Separação */}
          {activeTab === 'wms' && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <ScanLine className="w-5 h-5 text-orange-500" />
                WMS - Separação de Pedidos
              </h2>
              <WMSPanel supplierId={FORNECEDOR_ID} />
            </div>
          )}

          {/* TAB: Produtos */}
          {activeTab === 'produtos' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-400" />
                  Meu Catálogo de Produtos
                </h2>
                <Button 
                  onClick={() => setShowAddProductModal(true)}
                  className="bg-blue-500 hover:bg-blue-600 h-9"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Produto
                </Button>
              </div>

              {/* Info sobre campos obrigatórios */}
              <Card className="bg-gray-800/50 border-gray-700 p-3 mb-4">
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">📋 Campos obrigatórios para o Robô funcionar:</strong>{" "}
                  Nome, Categoria, <span className="text-blue-400">SKU PAI</span>, <span className="text-green-400">EAN PAI</span>, Preço e Estoque.
                  {" "}Se não tiver descrição/imagem, <span className="text-purple-400">a IA gera automaticamente!</span>
                </p>
              </Card>

              {/* Lista de Produtos */}
              <div className="space-y-3">
                {produtos.map((produto) => (
                  <Card key={produto.id} className={`bg-gray-800/50 border-gray-700 p-4 ${!produto.temDescricao || !produto.temImagem ? 'border-orange-500/50' : ''}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Imagem */}
                      <div className="w-20 h-20 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                        {produto.imagem ? (
                          <img src={produto.imagem} alt={produto.nome} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-500">
                            <Image className="w-8 h-8" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-white font-semibold">{produto.nome}</h4>
                          {getStatusBadge(produto.status)}
                        </div>
                        <p className="text-gray-400 text-xs mb-2 line-clamp-1">
                          {produto.descricao || <span className="text-orange-400">⚠️ Sem descrição - IA pode gerar</span>}
                        </p>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-0.5 bg-gray-700 rounded text-gray-300">
                            {produto.categoria}
                          </span>
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded flex items-center gap-1">
                            <Hash className="w-3 h-3" />
                            SKU: {produto.skuPai}
                          </span>
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded flex items-center gap-1">
                            <Barcode className="w-3 h-3" />
                            EAN: {produto.eanPai}
                          </span>
                        </div>
                      </div>

                      {/* Preço e Estoque */}
                      <div className="lg:w-28 text-center">
                        <p className="text-xs text-gray-500">Preço</p>
                        <p className="text-white font-bold">R$ {produto.preco.toFixed(2)}</p>
                      </div>

                      <div className="lg:w-20 text-center">
                        <p className="text-xs text-gray-500">Estoque</p>
                        <p className="text-white font-bold">{produto.estoque}</p>
                      </div>

                      {/* Ações */}
                      <div className="flex gap-2">
                        {(!produto.temDescricao || !produto.temImagem) && (
                          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-8">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Gerar com IA
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-gray-700 h-8">
                          <Edit className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* TAB: Financeiro */}
          {activeTab === 'financeiro' && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-orange-500" />
                Resumo Financeiro
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="bg-gray-800/50 border-gray-700 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">A Receber (Repasse)</p>
                      <p className="text-green-400 text-2xl font-bold">R$ {fornecedorData.aReceber.toLocaleString()}</p>
                    </div>
                  </div>
                </Card>

                <Card className="bg-gray-800/50 border-gray-700 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Faturamento Mês</p>
                      <p className="text-blue-400 text-2xl font-bold">R$ {fornecedorData.faturamentoMes.toLocaleString()}</p>
                    </div>
                  </div>
                </Card>

                <Card className="bg-gray-800/50 border-gray-700 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Comissões Pagas</p>
                      <p className="text-purple-400 text-2xl font-bold">R$ {fornecedorData.comissoesPagas.toLocaleString()}</p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="flex gap-3">
                <Button className="bg-green-500 hover:bg-green-600">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Relatório de Vendas
                </Button>
                <Button variant="outline" className="border-gray-700">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Histórico de Repasses
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Importação */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto py-8">
          <Card className="bg-gray-900 border-gray-700 max-w-2xl w-full my-auto">
              <div className="p-6">
                {!importProgress ? (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                          <Upload className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-white">Importar Catálogo</h2>
                          <p className="text-gray-400 text-sm">Envie seu catálogo e o robô organiza tudo para você</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => { setShowImportModal(false); setImportStep(1); }}>
                        <X className="w-5 h-5" />
                      </Button>
                    </div>

                    {/* Step 1: Escolher categoria */}
                    {importStep === 1 && (
                      <div>
                        <h3 className="text-white font-semibold mb-3">1. Selecione a Categoria do Catálogo</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
                          {categorias.map(cat => (
                            <Button
                              key={cat}
                              size="sm"
                              onClick={() => setSelectedCategory(cat)}
                              className={`h-9 text-xs ${selectedCategory === cat ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                            >
                              {cat}
                            </Button>
                          ))}
                        </div>

                        <Button
                          onClick={() => setImportStep(2)}
                          disabled={!selectedCategory}
                          className="w-full bg-orange-500 hover:bg-orange-600 h-11"
                        >
                          Próximo
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    )}

                    {/* Step 2: Upload e opções */}
                    {importStep === 2 && (
                      <div>
                        <h3 className="text-white font-semibold mb-3">2. Envie seu Arquivo</h3>
                        
                        {/* Upload area */}
                        <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center mb-4 hover:border-orange-500 transition-colors cursor-pointer">
                          <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-gray-500" />
                          <p className="text-white font-medium mb-1">Arraste seu arquivo aqui</p>
                          <p className="text-gray-400 text-sm mb-3">ou clique para selecionar</p>
                          <p className="text-gray-500 text-xs">Aceito: .xlsx, .csv, .xml</p>
                        </div>

                        {/* Campos obrigatórios */}
                        <Card className="bg-gray-800/50 border-gray-700 p-4 mb-4">
                          <h4 className="text-white font-semibold mb-2">📋 Seu arquivo deve conter:</h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-2 text-green-400">
                              <Check className="w-4 h-4" />
                              Nome do Produto
                            </div>
                            <div className="flex items-center gap-2 text-green-400">
                              <Check className="w-4 h-4" />
                              Preço
                            </div>
                            <div className="flex items-center gap-2 text-orange-400">
                              <Check className="w-4 h-4" />
                              SKU PAI (obrigatório)
                            </div>
                            <div className="flex items-center gap-2 text-orange-400">
                              <Check className="w-4 h-4" />
                              EAN PAI (obrigatório)
                            </div>
                            <div className="flex items-center gap-2 text-green-400">
                              <Check className="w-4 h-4" />
                              Estoque
                            </div>
                            <div className="flex items-center gap-2 text-gray-400">
                              <Check className="w-4 h-4" />
                              Descrição (opcional)
                            </div>
                          </div>
                        </Card>

                        {/* Opção IA */}
                        <Card 
                          className={`p-4 cursor-pointer transition-all mb-4 ${useAI ? 'bg-orange-500/20 border-orange-500' : 'bg-gray-800/50 border-gray-700'}`}
                          onClick={() => setUseAI(!useAI)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${useAI ? 'bg-orange-500' : 'bg-gray-700'}`}>
                                <Bot className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h4 className="text-white font-semibold">🤖 Usar IA para completar</h4>
                                <p className="text-gray-400 text-xs">A IA gera títulos, descrições e processa imagens automaticamente</p>
                              </div>
                            </div>
                            <div className={`w-6 h-6 rounded flex items-center justify-center ${useAI ? 'bg-orange-500' : 'bg-gray-700'}`}>
                              {useAI && <Check className="w-4 h-4 text-white" />}
                            </div>
                          </div>
                        </Card>

                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            onClick={() => setImportStep(1)}
                            className="flex-1 border-gray-700 h-11"
                          >
                            Voltar
                          </Button>
                          <Button
                            onClick={startImport}
                            className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 h-11"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Importar Catálogo
                          </Button>
                        </div>
                      </div>
                    )}

                    <Button
                      variant="ghost"
                      onClick={() => { setShowImportModal(false); setImportStep(1); }}
                      className="w-full mt-3 text-gray-400"
                    >
                      Cancelar
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                      <Bot className="w-10 h-10 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">🤖 Robô Trabalhando...</h2>
                    <p className="text-gray-400 mb-4">{importProgress.message}</p>
                    
                    <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                      <div 
                        className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${(importProgress.step / importProgress.total) * 100}%` }}
                      />
                    </div>
                    <p className="text-gray-500 text-sm">{importProgress.step} de {importProgress.total}</p>
                  </div>
                )}
              </div>
          </Card>
        </div>
      )}

      {/* Modal Adicionar Produto COMPLETO */}
      {showAddProductModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto py-8">
          <Card className="bg-gray-900 border-gray-700 max-w-3xl w-full my-auto">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-500" />
                    Cadastrar Produto
                  </h2>
                  <Button variant="ghost" size="sm" onClick={() => setShowAddProductModal(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-3">
                  {/* SECAO 1: Informacoes Basicas */}
                  <Card className="bg-gray-800/50 border-gray-700 p-3">
                    <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                      <Tag className="w-4 h-4 text-blue-400" /> Informacoes Basicas
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">Nome do Produto *</label>
                        <Input value={newProduct.nome} onChange={(e) => setNewProduct({...newProduct, nome: e.target.value})}
                          placeholder="Ex: Notebook Gamer RTX 4060" className="bg-gray-800 border-gray-700 h-8 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">Categoria *</label>
                        <select value={newProduct.categoria} onChange={(e) => setNewProduct({...newProduct, categoria: e.target.value})}
                          className="w-full bg-gray-800 border border-gray-700 rounded-md px-2 h-8 text-white text-sm">
                          <option value="">Selecione...</option>
                          {categorias.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">Marca *</label>
                        <Input value={newProduct.marca} onChange={(e) => setNewProduct({...newProduct, marca: e.target.value})}
                          placeholder="Ex: Dell, Samsung" className="bg-gray-800 border-gray-700 h-8 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">Modelo</label>
                        <Input value={newProduct.modelo} onChange={(e) => setNewProduct({...newProduct, modelo: e.target.value})}
                          placeholder="Ex: G15-5530" className="bg-gray-800 border-gray-700 h-8 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">Condição *</label>
                        <select value={newProduct.condicao} onChange={(e) => setNewProduct({...newProduct, condicao: e.target.value})}
                          className="w-full bg-gray-800 border border-gray-700 rounded-md px-2 h-8 text-white text-sm">
                          <option value="new">Novo</option>
                          <option value="used">Usado</option>
                          <option value="refurbished">Recondicionado</option>
                        </select>
                      </div>
                    </div>
                  </Card>

                  {/* SECAO 2: Codigos EAN/SKU */}
                  <Card className="bg-blue-500/10 border-blue-500/30 p-3">
                    <h4 className="text-blue-400 font-semibold text-sm mb-2 flex items-center gap-2">
                      <Barcode className="w-4 h-4" /> Codigos de Identificacao (OBRIGATORIOS)
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">SKU * <span className="text-gray-500">(Codigo interno)</span></label>
                        <Input value={newProduct.skuPai} onChange={(e) => setNewProduct({...newProduct, skuPai: e.target.value.toUpperCase()})}
                          placeholder="Ex: NB-GAMER-001" className="bg-gray-800 border-gray-700 h-8 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">EAN/GTIN * <span className="text-gray-500">(Codigo de barras)</span></label>
                        <Input value={newProduct.eanPai} disabled={newProduct.semGtin}
                          onChange={(e) => setNewProduct({...newProduct, eanPai: e.target.value.replace(/\D/g, '')})}
                          placeholder={newProduct.semGtin ? 'SEM GTIN' : 'Ex: 7891234567890'}
                          className="bg-gray-800 border-gray-700 h-8 text-sm" />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input type="checkbox" checked={newProduct.semGtin}
                        onChange={(e) => setNewProduct({...newProduct, semGtin: e.target.checked, eanPai: e.target.checked ? 'SEM GTIN' : ''})}
                        className="rounded border-gray-600" />
                      <span className="text-xs text-gray-300">SEM GTIN (produto artesanal/usado/sem código)</span>
                    </label>
                    <p className="text-[10px] text-blue-300 mt-1">EAN/GTIN obrigatório para produtos novos em todos os marketplaces</p>
                  </Card>

                  {/* SECAO 3: Preco e Estoque */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-300 mb-0.5">Preco de Venda (R$) *</label>
                      <Input type="number" value={newProduct.preco} onChange={(e) => setNewProduct({...newProduct, preco: e.target.value})}
                        placeholder="Ex: 2999.90" className="bg-gray-800 border-gray-700 h-8 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-300 mb-0.5">Estoque *</label>
                      <Input type="number" value={newProduct.estoque} onChange={(e) => setNewProduct({...newProduct, estoque: e.target.value})}
                        placeholder="Ex: 50" className="bg-gray-800 border-gray-700 h-8 text-sm" />
                    </div>
                  </div>

                  {/* SECAO 4: Dimensoes e Peso */}
                  <Card className="bg-gray-800/50 border-gray-700 p-3">
                    <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4 text-green-400" /> Dimensoes e Peso (para frete)
                    </h4>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">Peso (kg)</label>
                        <Input type="number" value={newProduct.peso} onChange={(e) => setNewProduct({...newProduct, peso: e.target.value})}
                          placeholder="1.5" className="bg-gray-800 border-gray-700 h-8 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">Altura (cm)</label>
                        <Input type="number" value={newProduct.altura} onChange={(e) => setNewProduct({...newProduct, altura: e.target.value})}
                          placeholder="10" className="bg-gray-800 border-gray-700 h-8 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">Largura (cm)</label>
                        <Input type="number" value={newProduct.largura} onChange={(e) => setNewProduct({...newProduct, largura: e.target.value})}
                          placeholder="30" className="bg-gray-800 border-gray-700 h-8 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-0.5">Comprimento (cm)</label>
                        <Input type="number" value={newProduct.comprimento} onChange={(e) => setNewProduct({...newProduct, comprimento: e.target.value})}
                          placeholder="40" className="bg-gray-800 border-gray-700 h-8 text-sm" />
                      </div>
                    </div>
                  </Card>

                  {/* SECAO 5: Descricao e Ficha Tecnica */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-300 mb-0.5">Descrição <span className="text-purple-400">(IA pode gerar)</span></label>
                      <textarea value={newProduct.descricao} onChange={(e) => setNewProduct({...newProduct, descricao: e.target.value})}
                        placeholder="Descreva o produto... ou deixe em branco para a IA gerar" rows={3}
                        className="w-full bg-gray-800 border border-gray-700 rounded-md px-2 py-1.5 text-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-300 mb-0.5">Ficha Tecnica <span className="text-gray-500">(melhora ranqueamento)</span></label>
                      <textarea value={newProduct.fichaTecnica} onChange={(e) => setNewProduct({...newProduct, fichaTecnica: e.target.value})}
                        placeholder="Processador: Intel i7&#10;RAM: 16GB&#10;Armazenamento: 512GB SSD" rows={3}
                        className="w-full bg-gray-800 border border-gray-700 rounded-md px-2 py-1.5 text-white text-sm" />
                    </div>
                  </div>

                  {/* SECAO 6: Marketplaces Destino */}
                  <Card className="bg-orange-500/10 border-orange-500/30 p-3">
                    <h4 className="text-orange-400 font-semibold text-sm mb-2">Publicar em quais Marketplaces?</h4>
                    <div className="grid grid-cols-5 gap-1.5">
                      {marketplacesDisponiveis.map(mp => (
                        <label key={mp.id} className={`flex items-center gap-1.5 cursor-pointer p-1.5 rounded border text-xs ${
                          newProduct.marketplaces.includes(mp.id) ? 'bg-orange-500/20 border-orange-500' : 'bg-gray-800/50 border-gray-700'
                        }`}>
                          <input type="checkbox" checked={newProduct.marketplaces.includes(mp.id)}
                            onChange={(e) => {
                              if (e.target.checked) setNewProduct({...newProduct, marketplaces: [...newProduct.marketplaces, mp.id]});
                              else setNewProduct({...newProduct, marketplaces: newProduct.marketplaces.filter(m => m !== mp.id)});
                            }} className="rounded border-gray-600 w-3 h-3" />
                          <span className="text-gray-200">{mp.name}</span>
                        </label>
                      ))}
                    </div>
                    <Button size="sm" variant="outline" className="mt-2 h-6 text-[10px] border-gray-600"
                      onClick={() => setNewProduct({...newProduct, marketplaces: marketplacesDisponiveis.map(m => m.id)})}>
                      Selecionar Todos
                    </Button>
                  </Card>

                  {/* SECAO 7: IA e Fotos */}
                  <div className="grid grid-cols-2 gap-2">
                    <Card className={`p-3 cursor-pointer transition-all ${newProduct.usarIA ? 'bg-purple-500/20 border-purple-500' : 'bg-gray-800/50 border-gray-700'}`}
                      onClick={() => setNewProduct({...newProduct, usarIA: !newProduct.usarIA})}>
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded flex items-center justify-center ${newProduct.usarIA ? 'bg-purple-500' : 'bg-gray-700'}`}>
                          {newProduct.usarIA && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <p className="text-white font-medium text-sm">IA gerar título e descrição</p>
                          <p className="text-gray-400 text-[10px]">Conteudo otimizado para cada marketplace</p>
                        </div>
                      </div>
                    </Card>
                    <Card className="bg-gray-800/50 border-gray-700 p-3">
                      <p className="text-white font-medium text-sm mb-1">Foto Principal</p>
                      <p className="text-[10px] text-gray-400">Fundo branco puro, sem logos ou marcas d'agua</p>
                      <p className="text-[10px] text-yellow-400 mt-1">A IA pode gerar imagem se nao tiver</p>
                    </Card>
                  </div>

                  {/* Regras importantes */}
                  <Card className="bg-yellow-500/10 border-yellow-500/30 p-2.5">
                    <p className="text-yellow-400 font-semibold text-xs mb-1">Requisitos Obrigatorios (todos marketplaces):</p>
                    <ul className="text-gray-300 text-[10px] space-y-0.5 ml-3 list-disc">
                      <li>EAN/GTIN obrigatório para produtos novos. Sem código? Marque "SEM GTIN"</li>
                      <li>Foto principal com fundo branco puro (sem logos, marcas d'agua)</li>
                      <li>Titulo claro: Produto + Marca + Modelo + Caracteristicas</li>
                      <li>Ficha tecnica completa melhora ranqueamento em todos marketplaces</li>
                      <li>SKU interno para controle de estoque</li>
                    </ul>
                  </Card>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button variant="outline" onClick={() => setShowAddProductModal(false)} className="flex-1 border-gray-700 h-9 text-sm">
                    Cancelar
                  </Button>
                  <Button className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 h-9 text-sm"
                    onClick={handleSaveProduct} disabled={savingProduct}>
                    {savingProduct ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Cadastrar Produto
                  </Button>
                </div>
              </div>
          </Card>
        </div>
      )}

      {/* Modal Carteira Digital Fornecedor */}
      {showCarteiraModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto py-8">
          <Card className="bg-gray-900 border-gray-700 max-w-lg w-full my-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Wallet className="w-6 h-6 text-green-400" />
                  Carteira do Fornecedor
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCarteiraModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Saldos */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Card className="bg-green-500/10 border-green-500/30 p-3 text-center">
                  <p className="text-gray-400 text-xs">Disponível</p>
                  <p className="text-green-400 text-lg font-bold">R$ {fornecedorData.saldoDisponivel.toFixed(2)}</p>
                </Card>
                <Card className="bg-blue-500/10 border-blue-500/30 p-3 text-center">
                  <p className="text-gray-400 text-xs">Lucro p/ Sacar</p>
                  <p className="text-blue-400 text-lg font-bold">R$ {fornecedorData.lucroDisponivel.toFixed(2)}</p>
                </Card>
                <Card className="bg-yellow-500/10 border-yellow-500/30 p-3 text-center">
                  <p className="text-gray-400 text-xs">Bloqueado</p>
                  <p className="text-yellow-400 text-lg font-bold">R$ {fornecedorData.saldoBloqueado.toFixed(2)}</p>
                </Card>
              </div>

              {/* Tabs da Carteira */}
              <div className="flex gap-2 mb-4">
                <Button
                  size="sm"
                  onClick={() => setCarteiraTab('resumo')}
                  className={`flex-1 h-8 ${carteiraTab === 'resumo' ? 'bg-orange-500' : 'bg-gray-700'}`}
                >
                  <History className="w-4 h-4 mr-1" />
                  Histórico
                </Button>
                <Button
                  size="sm"
                  onClick={() => setCarteiraTab('sacar')}
                  className={`flex-1 h-8 ${carteiraTab === 'sacar' ? 'bg-green-500' : 'bg-gray-700'}`}
                >
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                  Sacar
                </Button>
                <Button
                  size="sm"
                  onClick={() => setCarteiraTab('dados')}
                  className={`flex-1 h-8 ${carteiraTab === 'dados' ? 'bg-orange-500' : 'bg-gray-700'}`}
                >
                  <Building2 className="w-4 h-4 mr-1" />
                  Dados
                </Button>
              </div>

              {/* Tab Resumo/Histórico */}
              {carteiraTab === 'resumo' && (
                <div>
                  <p className="text-sm text-gray-400 mb-2 flex items-center gap-1">
                    <History className="w-4 h-4" />
                    Histórico de Transações
                  </p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {historicoCarteira.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg text-sm">
                        <div className="flex items-center gap-2">
                          {item.tipo === 'comissao' && <DollarSign className="w-4 h-4 text-green-400" />}
                          {item.tipo === 'saque' && <Banknote className="w-4 h-4 text-blue-400" />}
                          <div>
                            <p className="text-white">{item.descricao}</p>
                            <p className="text-gray-500 text-xs">{item.data}</p>
                          </div>
                        </div>
                        <span className={item.tipo === 'comissao' ? 'text-green-400' : 'text-blue-400'}>
                          {item.tipo === 'comissao' ? '+' : '-'} R$ {item.valor.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab Sacar */}
              {carteiraTab === 'sacar' && (
                <div>
                  <Card className="bg-green-500/10 border-green-500/30 p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Banknote className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-semibold">Sacar Lucros</span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      Saldo disponível para saque: <strong className="text-green-400">R$ {fornecedorData.lucroDisponivel.toFixed(2)}</strong>
                    </p>
                  </Card>

                  <div className="mb-4">
                    <label className="block text-sm text-gray-300 mb-2">Valor do Saque</label>
                    <Input 
                      type="number"
                      placeholder="R$ 0,00" 
                      value={valorSaque}
                      onChange={(e) => setValorSaque(e.target.value)}
                      className="bg-gray-800 border-gray-700 h-12 text-lg" 
                    />
                    <div className="flex gap-2 mt-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1 border-gray-700 h-8 text-xs"
                        onClick={() => setValorSaque((fornecedorData.lucroDisponivel * 0.5).toFixed(2))}
                      >
                        50%
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1 border-gray-700 h-8 text-xs"
                        onClick={() => setValorSaque(fornecedorData.lucroDisponivel.toFixed(2))}
                      >
                        100%
                      </Button>
                    </div>
                  </div>

                  {dadosBancarios.chavePix ? (
                    <Card className="bg-gray-800/50 border-gray-700 p-3 mb-4">
                      <p className="text-gray-400 text-xs mb-1">Conta para recebimento:</p>
                      <p className="text-white text-sm">{dadosBancarios.banco || 'Conta Cadastrada'}</p>
                      <p className="text-gray-400 text-xs">Chave PIX: {dadosBancarios.chavePix}</p>
                    </Card>
                  ) : (
                    <Card className="bg-yellow-500/10 border-yellow-500/30 p-3 mb-4">
                      <p className="text-yellow-400 text-sm">
                        ⚠️ Configure seus dados bancários antes de sacar
                      </p>
                    </Card>
                  )}

                  <Button 
                    className="w-full bg-green-500 hover:bg-green-600 h-12"
                    onClick={handleSaque}
                    disabled={!dadosBancarios.chavePix}
                  >
                    <ArrowUpRight className="w-5 h-5 mr-2" />
                    Solicitar Saque
                  </Button>
                </div>
              )}

              {/* Tab Dados Bancários */}
              {carteiraTab === 'dados' && (
                <div>
                  <Card className="bg-cyan-500/10 border-cyan-500/30 p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-5 h-5 text-cyan-400" />
                      <span className="text-cyan-400 font-semibold">Dados para Recebimento</span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      Configure sua conta bancária e chaves PIX
                    </p>
                  </Card>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Banco</label>
                      <Input
                        placeholder="Ex: Itaú, Nubank, Bradesco..."
                        value={dadosBancarios.banco}
                        onChange={(e) => setDadosBancarios({...dadosBancarios, banco: e.target.value})}
                        className="bg-gray-800 border-gray-700 h-9"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Agência</label>
                        <Input
                          placeholder="0000"
                          value={dadosBancarios.agencia}
                          onChange={(e) => setDadosBancarios({...dadosBancarios, agencia: e.target.value})}
                          className="bg-gray-800 border-gray-700 h-9"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Conta</label>
                        <Input
                          placeholder="00000-0"
                          value={dadosBancarios.conta}
                          onChange={(e) => setDadosBancarios({...dadosBancarios, conta: e.target.value})}
                          className="bg-gray-800 border-gray-700 h-9"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Tipo de Conta</label>
                      <select
                        value={dadosBancarios.tipoConta}
                        onChange={(e) => setDadosBancarios({...dadosBancarios, tipoConta: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 h-9 text-white text-sm"
                      >
                        <option value="corrente">Conta Corrente</option>
                        <option value="poupanca">Conta Poupança</option>
                      </select>
                    </div>

                    <div className="pt-2 border-t border-gray-700">
                      <label className="block text-xs text-gray-400 mb-1">Tipo de Chave PIX</label>
                      <select
                        value={dadosBancarios.tipoChavePix}
                        onChange={(e) => setDadosBancarios({...dadosBancarios, tipoChavePix: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 h-9 text-white text-sm mb-2"
                      >
                        <option value="cnpj">CNPJ</option>
                        <option value="cpf">CPF</option>
                        <option value="email">E-mail</option>
                        <option value="telefone">Telefone</option>
                        <option value="aleatoria">Chave Aleatória</option>
                      </select>
                      <Input
                        placeholder="Digite sua chave PIX..."
                        value={dadosBancarios.chavePix}
                        onChange={(e) => setDadosBancarios({...dadosBancarios, chavePix: e.target.value})}
                        className="bg-gray-800 border-gray-700 h-9"
                      />
                    </div>
                  </div>

                  <Button 
                    className="w-full mt-4 bg-orange-500 hover:bg-orange-600"
                    onClick={salvarDadosBancarios}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Salvar Dados Bancários
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Modal Perfil do Fornecedor */}
      {showPerfilModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto py-8">
          <Card className="bg-gray-900 border-gray-700 max-w-lg w-full my-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Dados da Empresa</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowPerfilModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Ícone / Foto */}
              <div className="text-center mb-4">
                <div
                  onClick={selecionarFoto}
                  className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-2 cursor-pointer hover:opacity-90 overflow-hidden"
                >
                  {fornecedorData.foto ? (
                    <img src={fornecedorData.foto} alt="Foto de perfil" className="w-full h-full object-cover" />
                  ) : (
                    <Building2 className="w-10 h-10 text-white" />
                  )}
                </div>
                <input
                  ref={inputFotoRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={enviarFoto}
                  className="hidden"
                />
                <Button size="sm" variant="outline" className="border-gray-700 text-xs mb-2" onClick={selecionarFoto} disabled={enviandoFoto}>
                  <Image className="w-3 h-3 mr-1" />
                  {enviandoFoto ? "Enviando..." : "Alterar Foto"}
                </Button>
                <p className="text-gray-400 text-sm">Dados do fornecedor</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Nome Fantasia</label>
                  <Input
                    value={fornecedorData.nome}
                    onChange={(e) => setFornecedorData({...fornecedorData, nome: e.target.value})}
                    className="bg-gray-800 border-gray-700 h-9"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Razão Social</label>
                  <Input
                    value={fornecedorData.razaoSocial}
                    onChange={(e) => setFornecedorData({...fornecedorData, razaoSocial: e.target.value})}
                    className="bg-gray-800 border-gray-700 h-9"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">CNPJ</label>
                  <Input
                    value={fornecedorData.cnpj}
                    onChange={(e) => setFornecedorData({...fornecedorData, cnpj: e.target.value})}
                    className="bg-gray-800 border-gray-700 h-9"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">E-mail</label>
                    <Input
                      value={fornecedorData.email}
                      onChange={(e) => setFornecedorData({...fornecedorData, email: e.target.value})}
                      className="bg-gray-800 border-gray-700 h-9"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Telefone</label>
                    <Input
                      value={fornecedorData.telefone}
                      onChange={(e) => setFornecedorData({...fornecedorData, telefone: e.target.value})}
                      className="bg-gray-800 border-gray-700 h-9"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Endereço</label>
                  <Input
                    value={fornecedorData.endereco}
                    onChange={(e) => setFornecedorData({...fornecedorData, endereco: e.target.value})}
                    className="bg-gray-800 border-gray-700 h-9"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Cidade</label>
                    <Input
                      value={fornecedorData.cidade}
                      onChange={(e) => setFornecedorData({...fornecedorData, cidade: e.target.value})}
                      className="bg-gray-800 border-gray-700 h-9"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Estado</label>
                    <Input
                      value={fornecedorData.estado}
                      onChange={(e) => setFornecedorData({...fornecedorData, estado: e.target.value})}
                      className="bg-gray-800 border-gray-700 h-9"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">CEP</label>
                    <Input
                      value={fornecedorData.cep}
                      onChange={(e) => setFornecedorData({...fornecedorData, cep: e.target.value})}
                      className="bg-gray-800 border-gray-700 h-9"
                    />
                  </div>
                </div>
              </div>

              <Button 
                className="w-full mt-4 bg-orange-500 hover:bg-orange-600"
                onClick={() => { toast.success("✅ Dados atualizados com sucesso!"); setShowPerfilModal(false); }}
              >
                <Check className="w-4 h-4 mr-2" />
                Salvar Alterações
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Fornecedor;
